import type { IListItemProps } from '@sixui/ui';
import { forwardRef, useContext } from 'react';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListItem } from '@sixui/ui';
import { listItemTokens } from '@sixui/ui/components/ListItem/ListItem.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import stylex from '@stylexjs/stylex';

import { useActiveLink } from '~/hooks/useActiveLink';
import { NavMenuContext } from './NavMenuContext';

export type INavMenuItemProps = Omit<
  IListItemProps,
  'ref' | 'as' | 'href' | 'active' | 'styles'
> & {
  children: React.ReactNode;
  href?: string;
  exact?: boolean;
  matchPatterns?: Array<string>;
  dim?: boolean;
};

const styles = stylex.create({
  trailingIcon: {
    marginLeft: '0.5rem',
  },
});

const listItemStyles = stylex.create({
  host: {
    [listItemTokens.containerShape]: shapeTokens.corner$sm,
  },
});

const dimListItemStyles = stylex.create({
  host: {
    [listItemTokens.containerShape]: shapeTokens.corner$sm,

    [listItemTokens.textColor]: colorSchemeTokens.onSurfaceVariant,
    [listItemTokens.textColor$hover]: colorSchemeTokens.onSurfaceVariant,
    [listItemTokens.textColor$focus]: colorSchemeTokens.onSurfaceVariant,
    [listItemTokens.textColor$pressed]: colorSchemeTokens.onSurfaceVariant,
  },
});

export const NavMenuItem = forwardRef(function NavMenuItem(
  props: INavMenuItemProps,
  ref?: React.Ref<HTMLButtonElement>,
) {
  const {
    sx,
    children,
    href,
    exact,
    matchPatterns,
    dim: dimProp,
    ...other
  } = props;
  const active = useActiveLink({ matchPatterns, href, exact });
  const context = useContext(NavMenuContext);

  const dim = dimProp ?? context?.dim;
  const isExternal = other.target === '_blank';

  return (
    <ListItem
      sx={sx}
      styles={dim ? dimListItemStyles : listItemStyles}
      selected={other.selected ?? active}
      href={href}
      ref={ref}
      {...other}
    >
      {children}
      {isExternal ? (
        <FontAwesomeIcon
          {...stylex.props(styles.trailingIcon)}
          icon={faArrowUpRightFromSquare}
          size="xs"
        />
      ) : null}
    </ListItem>
  );
});
